import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { propertiesApi } from '../services/api';

const HeroSection = () => {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Slide d'accueil fixe - images différentes pour mobile et desktop
  const welcomeSlide = {
    id: 'welcome',
    image: 'https://customer-assets.emergentagent.com/job_14bd1402-02f4-4dac-83c7-ac89ea1f2296/artifacts/wbge7hta_image.png',
    imageMobile: 'https://customer-assets.emergentagent.com/job_14bd1402-02f4-4dac-83c7-ac89ea1f2296/artifacts/ewqasfpw_image.png',
    title: 'Bienvenue chez Pam Immobilier',
    hero_description: 'Votre partenaire de confiance pour l\'achat et la vente de biens immobiliers.',
    link: '/a-propos'
  };

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    // Only use the welcome slide
    setSlides([welcomeSlide]);
  };

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      nextSlide();
    }, 6000);
    return () => clearInterval(timer);
  }, [currentSlide, slides.length]);

  const nextSlide = () => {
    if (!isAnimating && slides.length > 1) {
      setIsAnimating(true);
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const prevSlide = () => {
    if (!isAnimating && slides.length > 1) {
      setIsAnimating(true);
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  if (slides.length === 0) {
    return (
      <section className="relative h-screen min-h-[600px] bg-slate-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </section>
    );
  }

  return (
    <section className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-400 ease-in-out ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Desktop image */}
          <div
            className="absolute inset-0 bg-cover bg-center hidden md:block"
            style={{
              backgroundImage: `url(${slide.image})`
            }}
          />
          {/* Mobile image - uses imageMobile if available, otherwise falls back to image */}
          <div
            className="absolute inset-0 bg-cover bg-center block md:hidden"
            style={{
              backgroundImage: `url(${slide.imageMobile || slide.image})`
            }}
          />
          {/* Subtle overlay */}
          <div className="absolute inset-0 bg-black/10" />
        </div>
      ))}

      {/* Content Card - Bottom Right */}
      <div className="relative z-20 h-full">
        <div className="absolute bottom-6 right-4 md:bottom-12 md:right-16">
          <div
            key={currentSlide}
            className="bg-[#8B2942] p-3 md:p-8 max-w-[280px] md:max-w-md animate-in fade-in slide-in-from-right-8 duration-400"
          >
            <h2 className="text-base md:text-2xl font-bold text-white mb-2 md:mb-3 leading-tight line-clamp-2">
              {slides[currentSlide]?.title}
            </h2>
            {slides[currentSlide]?.hero_description && (
              <p className="text-xs md:text-base text-white/90 mb-3 md:mb-4 leading-relaxed line-clamp-2 md:line-clamp-3">
                {slides[currentSlide]?.hero_description}
              </p>
            )}
            <Button
              asChild
              size="sm"
              className="bg-white text-[#8B2942] hover:bg-white/90 font-semibold text-xs md:text-sm w-full md:w-auto"
            >
              <Link to={slides[currentSlide]?.link} className="flex items-center justify-center gap-2">
                PLUS D'INFOS
                <ArrowRight className="w-3 h-3 md:w-4 md:h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-3 text-white hover:scale-110 transition-all duration-300 drop-shadow-lg"
            aria-label="Slide précédent"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-3 text-white hover:scale-110 transition-all duration-300 drop-shadow-lg"
            aria-label="Slide suivant"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </>
      )}
    </section>
  );
};

export default HeroSection;
